import ModalComponent from '../../../../../components/ModalComponent';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import ButtonComponent from '../../../../../components/ButtonComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../../../components/DropSelectComponent';
import {
  CheckCircleRounded,
  CloseRounded,
  DeleteOutline,
  PendingActions,
  SearchTwoTone,
} from '@mui/icons-material';
import { useGetAllControllersForSelectionMutateAsync, useGetAllFacialControllerByLicenseAsync } from '../../../../../services/Devices/Recognition/Controllers/hooks/useMutation';
import { IFacialControllerSimpleProps } from '../../../../../services/Devices/Recognition/Controllers/types';
import {
  useGetAllInternalFacesFromDeviceAsync,
  useGetAllInternalFacesFromDeviceCountAsync,
} from '../../../../../services/Devices/hooks/useMutation';
import TableComponent, {
  IColumnProps,
} from '../../../../../components/TableComponent';
import { IRecognitionInternalProps } from '../../../../../services/Devices/types';
import { Box } from '@mui/material';
import TooltipComponent from '../../../../../components/TooltipComponent';
import ButtonActionsComponent, {
  IActionButtonProps,
} from '../../../../../components/ButtonActionsComponent';
import RecognitionsEditModal from './RecognitionsEditModal';
import {
  IRecognitionSimpleProps,
  IRecognitionTypes,
} from '../../../../../services/Recognition/types';
import { convertBase64ToBlob } from '../../../../../utils/TransformUtils';
import { IPaginationParams, ISelectionOption } from '../../../../../services/types';
import { timeout } from 'workbox-core/_private';
import { t } from 'i18next';

interface SyncFacesModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleCreateRecognition?: () => void;
  handleClose?: () => void;
}

const SyncFacesModal = ({
  setIsOpen,
  isOpen,
  handleCreateRecognition,
  handleClose,
}: SyncFacesModalProps) => {
  const [devices, setDevices] = useState<ISelectionOption[]>([]);
  const [devicesOptions, setDevicesOptions] = useState<IDropSelectOptions[]>(
    [],
  );
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [count, setCount] = useState(0);
  const [isOpenRecognitionModal, setIsOpenRecognitionModal] =
    useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(undefined);
  const [faces, setFaces] = useState<IRecognitionInternalProps[]>([]);
  const [facesToAdd, setFacesToAdd] = useState<any[]>([]);
  const [recognitionToUpdate, setRecognitionToUpdate] = useState<
    IRecognitionSimpleProps | undefined
  >(undefined);

  const getDeviceSelectionOptions = (
    dev: ISelectionOption[],
  ): IDropSelectOptions[] => {
    return dev.map((i) => {
      return {
        option: i.name,
        value: i.id,
      } as IDropSelectOptions;
    });
  };
  const { mutateAsync: mutateGetAllControllersForSelectionAsync } =
    useGetAllControllersForSelectionMutateAsync();

  const {
    mutateAsync: mutateGetAllInternalFacesFromDeviceAsync,
    isLoading: isLoadingGetAllInternalFacesFromDeviceAsync,
  } = useGetAllInternalFacesFromDeviceAsync();

  const {
    mutateAsync: mutateGetAllInternalFacesFromDeviceCountAsync,
    isLoading: isLoadingGetAllInternalFacesFromDeviceCountAsync,
  } = useGetAllInternalFacesFromDeviceCountAsync();

  const columns: IColumnProps[] = [
    {
      label: '',
      id: 'isSynchronized',
      render: (isSynchronized) => {
        return (
          <>
            {isSynchronized ? (
              <TooltipComponent
                title={t('recognitionPage.syncFacesModal.registeredInAlertAI')}
              >
                <CheckCircleRounded color={'success'} />
              </TooltipComponent>
            ) : (
              <TooltipComponent
                title={t(
                  'recognitionPage.syncFacesModal.noRegisteredInAlertAI',
                )}
              >
                <CloseRounded color={'error'} />
              </TooltipComponent>
            )}
          </>
        );
      },
    },
    { label: 'ID', id: 'userId', width: 0 },
    { label: t('recognitionPage.tableHeaders.name'), id: 'name' },
    {
      label: t('recognitionPage.tableHeaders.type'),
      id: 'userId',
      align: 'center',
      render: (userId) => {
        const face = faces.find((f) => f.userId == userId);
        if (!face) return <></>;
        switch (face.discriminator) {
          case IRecognitionTypes.CUSTOMER:
            return <>{t('recognitionPage.registerFace.customer')}</>;
          case IRecognitionTypes.STUDENT:
            return <>{t('recognitionPage.registerFace.student')}</>;
          case IRecognitionTypes.RESPONSIBLE:
            return <>{t('recognitionPage.registerFace.responsible')}</>;
          case IRecognitionTypes.GENERAL:
            return <>{t('recognitionPage.registerFace.general')}</>;
          default:
            return <>-</>;
        }
      },
    },
    {
      id: 'image',
      label: t('recognitionPage.tableHeaders.image'),
      align: 'center',
      width: 60,
      render: (image) => {
        return (
          <img
            src={`data:image/jpeg;base64,${image}`}
            alt="user_img"
            width={45}
            height={45}
            style={{
              objectFit: 'cover',
              borderRadius: '50%',
              cursor: 'pointer',
            }}
          />
        );
      },
    },
    {
      id: 'userId',
      label: t('recognitionPage.tableHeaders.actions'),
      align: 'center',
      width: 0,
      render: (userId) => {
        const face = faces.find((f) => f.userId == userId);
        if (!face) return <></>;
        return (
          <ButtonComponent
            disabled={face.isSynchronized}
            color={'success'}
            startIcon={<CheckCircleRounded />}
            variant={'outlined'}
            onClick={() => registerFace(face)}
          >
            Sincronizar
          </ButtonComponent>
        );
      },
    },
  ];

  const registerFace = (face: IRecognitionInternalProps) => {
    if (face) {
      const { name, userId, image } = face;
      const data: IRecognitionSimpleProps = {
        name,
        discriminator: face.discriminator,
        image: convertBase64ToBlob(image),
        userId,
        facialControllersIds: [selectedDevice],
        isTemporary: false,
      };

      setRecognitionToUpdate(data);
    }
    setIsOpenRecognitionModal(true);
  };

  const actionOptions: (userId: string) => IActionButtonProps[] = (
    userId: string,
  ) => {
    const face = faces.find((f) => f.userId == userId);

    return [
      {
        label: t('recognitionPage.syncFacesModal.deleteController'),
        icon: <DeleteOutline />,
        onClick: () => {},
        disabled: true,
      },
      {
        label: t('recognitionPage.syncFacesModal.fillInData'),
        icon: <PendingActions />,
        disabled: !!(face && face.discriminator),
        onClick: () => {
          if (face) {
            const { name, userId, image } = face;
            const data: IRecognitionSimpleProps = {
              name,
              discriminator: face.discriminator,
              image: convertBase64ToBlob(image),
              userId,
              facialControllersIds: [selectedDevice],
              isTemporary: false,
            };

            setRecognitionToUpdate(data);
          }
          setIsOpenRecognitionModal(true);
        },
      },
    ];
  };

  const handleGetFaces = async (
    pagination: IPaginationParams,
    isChangePage: boolean = false,
  ) => {
    if (selectedDevice) {
      if (!isChangePage) {
        await mutateGetAllInternalFacesFromDeviceCountAsync(
          selectedDevice,
        ).then(setCount);
      }
      await mutateGetAllInternalFacesFromDeviceAsync({
        id: selectedDevice,
        pagination: pagination,
      }).then(setFaces);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setFaces([]);
    setSelectedDevice(undefined);
    if (handleClose) handleClose();
  };

  useEffect(() => {
    if (isOpen) {
      mutateGetAllControllersForSelectionAsync().then((r) => {
        setDevices(r);
        setDevicesOptions(getDeviceSelectionOptions(r));
      });
    }
  }, [isOpen]);

  return (
    <ModalComponent
      title={t('recognitionPage.syncFacesModal.title')}
      isOpen={isOpen}
      width={faces.length > 0 ? 'auto' : undefined}
      onClose={() => onClose()}
    >
      <Box sx={{ mb: 4 }}>
        <DropSelectComponent
          valueSelected={selectedDevice}
          label={t('common.devices')}
          isRequired={false}
          additionalButton={
            <ButtonComponent
              disabled={!selectedDevice}
              onClick={() => handleGetFaces({ pageSize: 10, pageNumber: 1 })}
              loading={
                isLoadingGetAllInternalFacesFromDeviceAsync ||
                isLoadingGetAllInternalFacesFromDeviceCountAsync
              }
              sx={{ 
                borderTopLeftRadius: 0, 
                borderBottomLeftRadius: 0,
                height: '56px',
                boxShadow: 'none',
                marginTop: '0px'
              }}
            >
              <SearchTwoTone />
            </ButtonComponent>
          }
          options={devicesOptions}
          onChange={setSelectedDevice}
        />
      </Box>

      <TableComponent
        title={t('recognitionPage.syncFacesModal.facesFoundOnController')}
        rows={faces ?? []}
        isLoading={
          isLoadingGetAllInternalFacesFromDeviceAsync ||
          isLoadingGetAllInternalFacesFromDeviceCountAsync
        }
        totalCount={count}
        onChangePageOrRowsPerPage={(paginationParams) => {
          setPagination(paginationParams);
          handleGetFaces(paginationParams, true);
        }}
        emptyMessage={t('recognitionPage.syncFacesModal.noRegisterFaces')}
        columns={columns}
      />
      {recognitionToUpdate && (
        <RecognitionsEditModal
          isOpen={isOpenRecognitionModal}
          selectedRecognition={recognitionToUpdate}
          handleSaveForm={() => {
            if (handleCreateRecognition) handleCreateRecognition();
            handleGetFaces(pagination);
          }}
          handleClose={() => setIsOpenRecognitionModal(false)}
        />
      )}
    </ModalComponent>
  );
};

export default SyncFacesModal;
